import { useMemo, useState } from 'react';
import { Button, Stack, Typography } from '@mui/material';
import { JobSearch } from '@/components/partial/job-search';
import { BaseDialog } from '@/components/shared/base-dialog';
import { useRecommendationProfile } from '@/context/recommendation-profile';
import { useUpdateUserJobs, useUserJobsIndex } from '@/data/account/user-jobs';
import { Goal } from '@/models/recommendation';
import { JobType, type JobTitle } from '@/models/taxonomy/job';
import { EditObjective } from '@/packages/skill-management/components/occupations/edit-objective';

enum Steps {
  CurrentJob = 0,
  Objective = 1,
}

interface EditOccupationsDialogProps {
  onClose: () => void;
  onSuccess: () => void;
}

export const EditOccupationsDialog = ({
  onClose,
  onSuccess,
}: EditOccupationsDialogProps) => {
  const [step, setStep] = useState(Steps.CurrentJob);
  const { jobs } = useUserJobsIndex();
  const [currentJob, setCurrentJob] = useState<JobTitle | undefined>(
    jobs?.current
  );
  const { updateProfile, isUpdating, profile } = useRecommendationProfile();
  const [goal, setGoal] = useState<Goal | undefined>(
    profile?.goal ?? undefined
  );
  const [desiredJob, setDesiredJob] = useState<JobTitle | undefined>(
    jobs?.desired
  );
  const { mutate: updateUserJobs, isPending: isUpdatingUserJobs } =
    useUpdateUserJobs();

  const isPending = isUpdatingUserJobs || isUpdating;

  const handleNext = () => {
    setStep(Steps.Objective);
  };

  const handleBack = () => {
    setStep(Steps.CurrentJob);
  };

  const isCareerChange = goal === Goal.CareerChange;

  const handleSave = () => {
    if (currentJob?.id) {
      updateUserJobs({
        title_id: currentJob?.id,
        job_type: JobType.Current,
      });
    }

    if (isCareerChange && desiredJob?.id) {
      updateUserJobs({
        title_id: desiredJob?.id,
        job_type: JobType.Desired,
      });
    }

    updateProfile({
      profile: {
        id: profile!.id,
        current_job: currentJob,
        desired_job: isCareerChange ? desiredJob : undefined,
        goal,
      },
    });

    onSuccess();
  };

  const isCurrentJobStep = step === Steps.CurrentJob;
  const action = {
    cancel: {
      label: isCurrentJobStep ? 'Cancelar' : 'Voltar',
      onClick: isCurrentJobStep ? onClose : handleBack,
    },
    confirm: {
      label: isCurrentJobStep ? 'Próximo' : 'Salvar',
      onClick: isCurrentJobStep ? handleNext : handleSave,
    },
  };

  const isFormValid = useMemo(() => {
    if (isCurrentJobStep) {
      return currentJob !== null;
    }

    if (goal === Goal.CareerChange) {
      return desiredJob !== null;
    }

    return true;
  }, [isCurrentJobStep, currentJob, goal, desiredJob]);

  return (
    <BaseDialog
      title="Objetivo e Cargo"
      onClose={onClose}
      open={true}
      actionsComponent={
        <>
          <Button variant="text" onClick={action.cancel.onClick}>
            {action.cancel.label}
          </Button>
          <Button
            variant="contained"
            onClick={action.confirm.onClick}
            disabled={!isFormValid}
            loading={isPending}
          >
            {action.confirm.label}
          </Button>
        </>
      }
    >
      <Stack gap={2} minWidth={400} mt={1}>
        {isCurrentJobStep ? (
          <>
            <Typography>Cargo atual</Typography>
            <JobSearch
              jobTitle={currentJob ?? null}
              onSelect={(value) => {
                setCurrentJob(value ?? undefined);
              }}
            />
          </>
        ) : (
          <EditObjective
            value={{ goal, desiredJob }}
            onSelect={(value) => {
              setGoal(value.goal);
              setDesiredJob(value.desiredJob);
            }}
          />
        )}
      </Stack>
    </BaseDialog>
  );
};
